import { useEffect, useRef, useState } from 'react';

const stats = [
  { icon: 'ri-trophy-line', value: 20, suffix: '+', label: 'Years of Experience' },
  { icon: 'ri-group-line', value: 150, suffix: '+', label: 'Clients Served' },
  { icon: 'ri-funds-line', value: 2.4, suffix: 'B+', label: 'Revenue Impacted', decimal: true },
  { icon: 'ri-bar-chart-grouped-line', value: 95, suffix: '%', label: 'Client Satisfaction' },
];

const reasons = [
  {
    icon: 'ri-shield-check-line',
    title: 'C-Suite Expertise',
    description:
      'Our consultants have held executive roles across industries, bringing real-world leadership experience to every engagement.',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Results-Driven Approach',
    description:
      'We focus on measurable outcomes — every strategy we develop is tied to clear KPIs and accountability frameworks.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Deep Texas Roots',
    description:
      'With decades of experience in the Texas business landscape, we understand the local market dynamics that drive success.',
  },
  {
    icon: 'ri-links-line',
    title: 'Trusted by PE & VC Firms',
    description:
      'We have a proven track record of preparing businesses for successful exits to private equity and venture capital firms.',
  },
];

function AnimatedNumber({ target, suffix, decimal }: { target: number; suffix: string; decimal?: boolean }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(parseFloat(current.toFixed(decimal ? 1 : 0)));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, decimal]);

  return (
    <span ref={ref}>
      {decimal ? count.toFixed(1) : count}
      {suffix}
    </span>
  );
}

export default function WhyChooseUs() {
  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-texas-red text-xs font-bold tracking-widest uppercase mb-3 block">Our Track Record</span>
          <h2 className="text-3xl md:text-4xl font-bold text-texas-navy mb-3">
            Why Choose Us
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            We bring decades of hands-on executive experience and a relentless focus on results to every client engagement.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col items-center text-center bg-texas-navy rounded-lg py-8 px-4"
            >
              <div className="w-12 h-12 flex items-center justify-center mb-3 text-texas-red">
                <i className={`${stat.icon} text-3xl`} />
              </div>
              <p className="text-3xl md:text-4xl font-bold text-white leading-none mb-1">
                <AnimatedNumber target={stat.value} suffix={stat.suffix} decimal={stat.decimal} />
              </p>
              <p className="text-sm text-white/55 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason.title}
              className="flex gap-4 items-start bg-texas-red-light rounded-lg p-6 border-l-4 border-texas-red"
            >
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-texas-red/10 rounded-md text-texas-red">
                <i className={`${reason.icon} text-xl`} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-texas-navy mb-1">{reason.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{reason.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
